import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren
} from '@angular/core';
import { ToastService } from '../../shared/toast/toast.service';
import { TrendsService, TrendPost, TrendComment } from '../../services/trends.service';
import { SellerService } from '../../services/seller.service';

@Component({
    selector: 'app-trends-page',
    templateUrl: './trends.component.html',
    styleUrls: ['./trends.component.scss'],
    standalone: false
})
export class TrendsComponent implements OnInit, AfterViewInit, OnDestroy {

  posts: TrendPost[] = [];
  loading = false;
  hasMore = true;
  page = 1;
  activeReviewPostId: number | null = null;
  reviewTexts: Record<number, string> = {};
  reviewStars: Record<number, number> = {};
  submitting = false;
  failedVideos: Record<number, true> = {};
  failedAvatars: Record<number, true> = {};
  expandedDescriptions: Record<number, true> = {};
  currentPlayingId: number | null = null;
  expandedComments: Record<number, boolean> = {};
  feedCommentLimits: Record<number, number> = {};
  followBusy: Record<number, boolean> = {};
  mediaPost: TrendPost | null = null;
  modalCommentLimits: Record<number, number> = {};
  replyTarget: { postId: number; comment: TrendComment } | null = null;
  replyText = '';
  replySubmitting = false;
  expandedReplyThreads: Record<string, boolean> = {};

  @ViewChildren('videoEl') videoElements!: QueryList<ElementRef<HTMLVideoElement>>;
  private videoObservers: IntersectionObserver[] = [];
  private currentVideo: HTMLVideoElement | null = null;
  postReactionMenuId: number | null = null;
  private postReactionPressTimer: ReturnType<typeof setTimeout> | null = null;
  private postLongPressTriggered = false;

  get activePost(): TrendPost | null {
    return this.posts.find(p => p.productId === this.activeReviewPostId) ?? null;
  }

  constructor(
    private trendsService: TrendsService,
    private sellerService: SellerService,
    private toastService: ToastService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadMore();
  }

  ngAfterViewInit(): void {
    this.videoElements.changes.subscribe(() => this.setupVideoObservers());
  }

  ngOnDestroy(): void {
    this.teardownVideoObservers();
    this.clearPostReactionTimer();
    document.body.style.overflow = '';
  }

  private setupVideoObservers(): void {
    this.teardownVideoObservers();
    this.videoElements.forEach(ref => {
      const video = ref.nativeElement;
      const postId = Number(video.dataset['postId']);
      const obs = new IntersectionObserver(entries => {
        const entry = entries[0];
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          if (this.currentVideo && this.currentVideo !== video) {
            this.currentVideo.pause();
          }
          video.muted = true;
          video.play().catch(() => {});
          this.currentVideo = video;
          this.currentPlayingId = postId;
          this.cdRef.detectChanges();
        } else {
          video.pause();
          if (this.currentVideo === video) {
            this.currentVideo = null;
            this.currentPlayingId = null;
            this.cdRef.detectChanges();
          }
        }
      }, { threshold: 0.5 });
      obs.observe(video);
      this.videoObservers.push(obs);
    });
  }

  private teardownVideoObservers(): void {
    this.videoObservers.forEach(o => o.disconnect());
    this.videoObservers = [];
  }

  loadMore(): void {
    if (this.loading || !this.hasMore) return;
    this.loading = true;
    this.trendsService.fetchTrends(this.page).subscribe({
      next: (result) => {
        this.posts = [...this.posts, ...result.posts];
        this.hasMore = result.hasMore;
        if (result.posts.length > 0) this.page++;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toastService.error('שגיאה בטעינת הטרנדים');
      }
    });
  }

  toggleLike(post: TrendPost): void {
    this.reactToPost(post, 'like');
  }

  postReactionEmoji(reaction: string | null): string {
    return ({ like: '👍', rocket: '🚀', fire: '🔥', ice: '❄️', giggles: '😆' } as Record<string, string>)[reaction || 'like'] || '👍';
  }

  startPostReactionPress(event: Event, post: TrendPost): void {
    event.stopPropagation();
    this.clearPostReactionTimer();
    this.postLongPressTriggered = false;
    this.postReactionPressTimer = setTimeout(() => {
      this.postLongPressTriggered = true;
      this.postReactionMenuId = post.productId;
    }, 500);
  }

  finishPostReactionPress(event: Event, post: TrendPost): void {
    event.stopPropagation();
    this.clearPostReactionTimer();
    if (!this.postLongPressTriggered) this.reactToPost(post, 'like');
  }

  cancelPostReactionPress(): void {
    this.clearPostReactionTimer();
  }

  choosePostReaction(event: Event, post: TrendPost, reaction: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.postReactionMenuId = null;
    this.reactToPost(post, reaction);
  }

  reactToPost(post: TrendPost, reaction: string): void {
    this.trendsService.reactToPost(post.productId, reaction).subscribe({
      next: response => {
        post.postReactionCounts = response.reaction_counts || {};
        post.userPostReaction = response.user_reaction;
        post.likesNumber = response.total || 0;
        post.userLovesIt = response.user_reaction === 'like';
      },
      error: error => this.toastService.error(error?.status === 401 || error?.status === 403 ? 'יש להתחבר כדי להגיב' : 'לא הצלחנו לשמור את התגובה')
    });
  }

  @HostListener('document:pointerdown')
  closePostReactionMenu(): void {
    this.postReactionMenuId = null;
  }

  private clearPostReactionTimer(): void {
    if (this.postReactionPressTimer) clearTimeout(this.postReactionPressTimer);
    this.postReactionPressTimer = null;
  }

  openReview(postId: number, initialText?: string): void {
    this.activeReviewPostId = postId;
    if (initialText) this.reviewTexts[postId] = initialText;
    if (!this.reviewStars[postId]) this.reviewStars[postId] = 4;
  }

  closeReview(): void {
    this.activeReviewPostId = null;
  }

  toggleComments(postId: number): void {
    this.expandedComments[postId] = !this.expandedComments[postId];
    if (this.expandedComments[postId] && !this.feedCommentLimits[postId]) {
      this.feedCommentLimits[postId] = 10;
    }
  }

  visibleComments(post: TrendPost) {
    const topLevel = post.comments.filter(comment => !comment.parentId);
    if (!this.expandedComments[post.productId]) return topLevel.slice(0, 2);
    return topLevel.slice(0, this.feedCommentLimits[post.productId] || 10);
  }

  modalComments(post: TrendPost): TrendComment[] {
    return post.comments.filter(comment => !comment.parentId)
      .slice(0, this.modalCommentLimit(post.productId));
  }

  repliesFor(post: TrendPost, comment: TrendComment): TrendComment[] {
    if (!comment.id) return [];
    return post.comments.filter(reply => reply.parentId === comment.id);
  }

  replyThreadKey(postId: number, comment: TrendComment): string {
    return `${postId}:${comment.id ?? 'pending'}`;
  }

  toggleReplyThread(postId: number, comment: TrendComment): void {
    const key = this.replyThreadKey(postId, comment);
    this.expandedReplyThreads[key] = !this.expandedReplyThreads[key];
  }

  isReplyThreadOpen(postId: number, comment: TrendComment): boolean {
    return !!this.expandedReplyThreads[this.replyThreadKey(postId, comment)];
  }

  onFeedCommentsScroll(event: Event, post: TrendPost): void {
    const element = event.currentTarget as HTMLElement;
    if (element.scrollTop + element.clientHeight < element.scrollHeight - 60) return;
    const current = this.feedCommentLimits[post.productId] || 10;
    if (current < post.comments.length) {
      this.feedCommentLimits[post.productId] = current + 10;
    }
  }

  openMedia(post: TrendPost): void {
    if (!post.imageUrl && !post.videoUrl) return;
    this.currentVideo?.pause();
    this.mediaPost = post;
    this.modalCommentLimits[post.productId] = 4;
    document.body.style.overflow = 'hidden';
  }

  closeMedia(): void {
    this.mediaPost = null;
    document.body.style.overflow = '';
  }

  modalCommentLimit(postId: number): number {
    return this.modalCommentLimits[postId] || 4;
  }

  hasMoreModalComments(post: TrendPost): boolean {
    return this.modalCommentLimit(post.productId) < post.comments.length;
  }

  loadMoreModalComments(post: TrendPost): void {
    if (!this.hasMoreModalComments(post)) return;
    this.modalCommentLimits[post.productId] = this.modalCommentLimit(post.productId) + 4;
  }

  onModalCommentsScroll(event: Event, post: TrendPost): void {
    const element = event.currentTarget as HTMLElement;
    if (element.scrollTop + element.clientHeight >= element.scrollHeight - 80) {
      this.loadMoreModalComments(post);
    }
  }

  toggleFollow(event: Event, post: TrendPost): void {
    event.preventDefault();
    event.stopPropagation();
    const id = post.sellerId;
    if (!id || post.sellerIsSelf || this.followBusy[id]) return;
    const wasFollowing = post.sellerIsFollowing;
    post.sellerIsFollowing = !wasFollowing;
    this.followBusy[id] = true;
    const request = wasFollowing ? this.sellerService.unfollow(id) : this.sellerService.follow(id);
    request.subscribe({
      next: result => {
        this.followBusy[id] = false;
        this.posts.filter(item => item.sellerId === id)
          .forEach(item => item.sellerIsFollowing = !!result.following);
        this.toastService.success(result.following ? 'התחלת לעקוב אחרי המוכר' : 'המעקב הוסר');
      },
      error: error => {
        this.followBusy[id] = false;
        post.sellerIsFollowing = wasFollowing;
        this.toastService.error(error?.status === 401 || error?.status === 403
          ? 'יש להתחבר כדי לעקוב'
          : 'לא הצלחנו לעדכן את המעקב');
      }
    });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.mediaPost) this.closeMedia();
    else if (this.activePost) this.closeReview();
  }

  submitReview(post: TrendPost): void {
    const postId = post.productId;
    const stars = this.reviewStars[postId];
    const text = (this.reviewTexts[postId] || '').trim();

    if (!stars || stars < 1) {
      this.toastService.warning('יש לבחור דירוג', { duration: 2500 });
      return;
    }
    if (!text) {
      this.toastService.warning('יש להזין תגובה', { duration: 2500 });
      return;
    }

    const optimisticComment: TrendComment = { id: null, username: 'אתה', text, timeAgo: 'עכשיו', starRating: stars, parentId: null, replyToUsername: null, reactionCounts: {}, userReaction: null };
    post.comments = [optimisticComment, ...post.comments];

    this.submitting = true;
    this.trendsService.submitReview(postId, text, stars).subscribe({
      next: () => {
        this.reviewTexts[postId] = '';
        this.reviewStars[postId] = 4;
        this.activeReviewPostId = null;
        this.submitting = false;
        this.toastService.success('התגובה נשלחה בהצלחה');
      },
      error: () => {
        post.comments = post.comments.filter(c => c !== optimisticComment);
        this.submitting = false;
        this.toastService.error('שגיאה בשליחת התגובה');
      }
    });
  }

  startReply(postId: number, comment: TrendComment): void {
    if (!comment.id) {
      this.toastService.warning('לא ניתן להשיב לתגובה שעדיין לא נשמרה');
      return;
    }
    this.replyTarget = { postId, comment };
    this.replyText = '';
  }

  cancelReply(): void {
    this.replyTarget = null;
    this.replyText = '';
  }

  isReplyingTo(postId: number, comment: TrendComment): boolean {
    return this.replyTarget?.postId === postId && this.replyTarget.comment === comment;
  }

  submitReply(post: TrendPost): void {
    const target = this.replyTarget;
    const text = this.replyText.trim();
    if (!target || target.postId !== post.productId || !target.comment.id || this.replySubmitting) return;
    if (!text) {
      this.toastService.warning('יש לכתוב תשובה');
      return;
    }
    const reply: TrendComment = { id: null, username: 'אתה', text, timeAgo: 'עכשיו', starRating: 0, parentId: target.comment.id, replyToUsername: target.comment.username, reactionCounts: {}, userReaction: null };
    const targetIndex = post.comments.indexOf(target.comment);
    post.comments.splice(targetIndex + 1, 0, reply);
    this.replySubmitting = true;
    this.trendsService.submitReply(post.productId, text, target.comment.id).subscribe({
      next: response => {
        reply.id = typeof response?.id === 'number' ? response.id : null;
        if (typeof response?.parent_review_id === 'number') {
          reply.parentId = response.parent_review_id;
        }
        this.replySubmitting = false;
        this.cancelReply();
        this.toastService.success('התשובה פורסמה');
      },
      error: () => {
        post.comments = post.comments.filter(item => item !== reply);
        this.replySubmitting = false;
        this.toastService.error('לא הצלחנו לפרסם את התשובה');
      }
    });
  }

  reactToComment(comment: TrendComment, reaction: string): void {
    if (!comment.id) return;
    this.trendsService.reactToComment(comment.id, reaction).subscribe({
      next: response => {
        comment.reactionCounts = response.reaction_counts || {};
        comment.userReaction = response.user_reaction;
      },
      error: error => this.toastService.error(
        error?.status === 401 || error?.status === 403 ? 'יש להתחבר כדי להגיב' : 'לא הצלחנו לשמור את התגובה'
      )
    });
  }

  getInitials(name: string): string {
    if (!name) return '??';
    return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');
  }

  getSuggestions(resourceType: string): string[] {
    const type = (resourceType || '').toUpperCase();
    if (type === 'CAR') {
      return ['נסיעה נהדרת', 'רכב מצוין', 'שירות מעולה', 'מחיר הוגן', 'מצב טוב'];
    } else if (type === 'NADLAN' || type === 'NADLANBUSINESS') {
      return ['נכס מושקע', 'מיקום מעולה', 'שכנים טובים', 'תמורה טובה', 'שווה לבדוק'];
    } else if (['SALE2', 'CELLULAR', 'FURNITURE', 'FASHION', 'PET', 'BOYBABY'].includes(type)) {
      return ['פריט איכותי', 'מחיר טוב', 'מצב מצוין', 'שווה הכסף', 'ממליץ בחום'];
    } else if (['BUSINESS', 'COWORKER', 'TEACHER', 'TOURISM'].includes(type)) {
      return ['שירות מקצועי', 'ממליץ בחום', 'מחיר הוגן', 'זמין ומהיר', 'עבודה מצוינת'];
    } else {
      return ['מומלץ', 'שווה לבדוק', 'תמורה טובה', 'שביעות רצון', 'מעולה'];
    }
  }

  getStarLabel(stars: number): string {
    return ({ 1: 'גרוע', 2: 'סביר', 3: 'טוב', 4: 'מצוין', 5: 'מושלם' } as Record<number, string>)[stars] || '';
  }

  setReviewStar(postId: number, star: number): void {
    this.reviewStars[postId] = star;
  }

  onVideoError(postId: number): void {
    this.failedVideos = { ...this.failedVideos, [postId]: true };
  }

  onAvatarError(postId: number): void {
    this.failedAvatars = { ...this.failedAvatars, [postId]: true };
  }

  isDescriptionLong(text: string): boolean {
    return text.length > 120;
  }

  toggleDescription(postId: number): void {
    if (this.expandedDescriptions[postId]) {
      const { [postId]: _, ...rest } = this.expandedDescriptions;
      this.expandedDescriptions = rest as Record<number, true>;
    } else {
      this.expandedDescriptions = { ...this.expandedDescriptions, [postId]: true };
    }
  }

  onVideoMouseEnter(video: HTMLVideoElement, postId: number): void {
    if (this.currentVideo && this.currentVideo !== video) {
      this.currentVideo.pause();
    }
    video.muted = true;
    video.play().catch(() => {});
    this.currentVideo = video;
    this.currentPlayingId = postId;
  }

  onVideoMouseLeave(video: HTMLVideoElement): void {
    video.pause();
    if (this.currentVideo === video) {
      this.currentVideo = null;
      this.currentPlayingId = null;
    }
  }

  applySuggestion(postId: number, suggestion: string): void {
    this.openReview(postId, suggestion);
  }

  @HostListener('window:scroll')
  onScroll(): void {
    if (this.loading || !this.hasMore) return;
    const scrollY = window.scrollY || window.pageYOffset;
    const viewportH = window.innerHeight;
    const docH = document.documentElement.scrollHeight;
    if (scrollY + viewportH >= docH - 300) this.loadMore();
  }

  trackByPostId(_: number, post: TrendPost): number {
    return post.productId;
  }
}
